import { useState } from "react"
import {
  Dialog,
  Checkbox,
  DialogBody,
  DialogFooter,
  FormGroup,
} from "@blueprintjs/core"
import React from "react"
import CustomButton from "components/Atoms/CustomButton"
import { Files } from "@phosphor-icons/react"
import { useMhCodeSelectorQuery } from "generated/graphql"
import { useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { ApplicationState } from "store"
import { updatePallet } from "store/production.reducer"
import CustomTag from "components/Atoms/CustomTag"

interface MHCodeSelectorProps {
  index: number
  productionData: any
}

const MHCodeSelector: React.FC<MHCodeSelectorProps> = ({
  index,
  productionData,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempSelected, setTempSelected] = useState<any[]>([])
  const [manualAssignments, setManualAssignments] = useState<Record<string, number>>({})
  const { id } = useParams()
  const dispatch = useDispatch()
  const [isReplace, setIsReplace] = useState(false)


  const production = useSelector(
    (state: ApplicationState) => state.production.production
  )

  const pallets = production?.pallets ?? []
  const pallet = pallets[index]

  const { data } = useMhCodeSelectorQuery({
    variables: { id },
    fetchPolicy: "network-only",
    skip: !isOpen,
  })

  const approvedProfiles =
    data?.production?.shipmentPlan?.approvedtradeprofileSet ?? []

  // all MH codes already assigned in any pallet
  const assignedMhCodes = pallet?.mhCodes?.map(mh => mh.id) ?? []


  const toggleSelect = (profile: any) => {
    setTempSelected(prev =>
      prev.find(p => p.id === profile.id)
        ? prev.filter(p => p.id !== profile.id)
        : [...prev, profile]
    )
  }



  const mixConfirmSelection = () => {
    if (!tempSelected.length) return

    const varieties = productionData?.shipmentPlan?.varieties ?? []
    if (!varieties.length) return

    const updatedPallets = [...pallets]
    const capacity = data?.production?.shipmentPlan?.skuType?.capacity ?? 1


    const varietyQueues: Record<string, any[]> = {}
    varieties.forEach(v => {
      const profiles = tempSelected.filter(
        p => p.tradeProfile?.plotCommodity?.variety?.id === v.id
      )
      varietyQueues[v.id] = profiles.map(p => {
        const totalAssignedBoxes = pallets.reduce((sum, pallet) => {
          const mhMatch = pallet.mhCodes?.find(mh => mh.id === p.id)
          return sum + (mhMatch?.boxes ?? 0)
        }, 0)

        const totalAssignedWeightKg = totalAssignedBoxes * capacity
        const mainYield = p.tradeProfile?.availableYield ?? 0
        const extraYield = p.tradeProfile?.extraAvailableYield ?? 0
        const totalYield = mainYield + extraYield

        return ({
          ...p,
          remaining:
            ((totalYield) - (totalAssignedWeightKg ?? 0)) /
            (capacity || 1),
        })
      })
    })


    if (isReplace) {
      const updatedPalletsCopy = [...pallets]
      const meta = productionData?.shipmentPlan?.metadata
        ? JSON.parse(productionData.shipmentPlan.metadata)
        : {}

      for (let pIndex = index; pIndex < updatedPalletsCopy.length; pIndex++) {
        const pallet = updatedPalletsCopy[pIndex]
        const clearedPallet = { ...pallet, mhCodes: [] }
        dispatch(updatePallet(clearedPallet, pIndex))
      }

      const varieties = productionData?.shipmentPlan?.varieties ?? []
      const capacity = data?.production?.shipmentPlan?.skuType?.capacity ?? 1

      const varietyGroups: Record<string, any[]> = {}
      varieties.forEach(v => {
        varietyGroups[v.id] = tempSelected.filter(
          p => p.tradeProfile?.plotCommodity?.variety?.id === v.id
        )
      })

      for (let pIndex = index; pIndex < updatedPalletsCopy.length; pIndex++) {
        const pallet = updatedPalletsCopy[pIndex]
        const requiredBoxes = pallet.isEuro
          ? meta.euro_pallet_box_count ?? 0
          : meta.regular_pallet_box_count ?? 0

        const perVariety = requiredBoxes / varieties.length
        let mhAssignments: any[] = []
        let allocated = 0

        // First, apply manual assignments if any exist for the first pallet being replaced
        const hasManualAssignments = Object.values(manualAssignments).some(val => val > 0)
        const manuallyAssignedIds = new Set<string>()

        if (hasManualAssignments && pIndex === index) {
          // Only apply manual assignments to the first pallet (index)
          for (const profileId in manualAssignments) {
            const manualBoxes = manualAssignments[profileId]
            if (manualBoxes > 0 && allocated < requiredBoxes) {
              const profile = tempSelected.find(p => p.id === profileId)
              if (profile) {
                const totalAssignedBoxes = pallets.reduce((sum, pal) => {
                  const mhMatch = pal.mhCodes?.find(mh => mh.id === profileId)
                  return sum + (mhMatch?.boxes ?? 0)
                }, 0)
                const totalAssignedWeightKg = totalAssignedBoxes * capacity
                const mainYield = profile.tradeProfile?.availableYield ?? 0
                const extraYield = profile.tradeProfile?.extraAvailableYield ?? 0
                const totalYield = mainYield + extraYield
                const availableBoxes = ((totalYield) - totalAssignedWeightKg) / capacity

                const assignBoxes = Math.min(manualBoxes, availableBoxes, requiredBoxes - allocated)

                if (assignBoxes > 0) {
                  mhAssignments.push({
                    id: profileId,
                    mhCode: profile.tradeProfile?.certificateNumber ?? "",
                    boxes: assignBoxes,
                  })
                  allocated += assignBoxes
                  manuallyAssignedIds.add(profileId)

                  // Update the variety group to reflect this assignment
                  const varietyId = profile.tradeProfile?.plotCommodity?.variety?.id
                  if (varietyId && varietyGroups[varietyId]) {
                    const groupProfile = varietyGroups[varietyId].find(p => p.id === profileId)
                    if (groupProfile) {
                      // Calculate new remaining after this assignment
                      const newTotalAssigned = totalAssignedBoxes + assignBoxes
                      const newTotalAssignedWeight = newTotalAssigned * capacity
                      const newRemaining = ((groupProfile.tradeProfile?.availableYield ?? 0) - newTotalAssignedWeight) / capacity
                      groupProfile.remaining = newRemaining
                    }
                  }
                }
              }
            }
          }
        }

        // Then, use automatic distribution for any remaining quota
        for (const v of varieties) {
          let quota = perVariety - (allocated / varieties.length)
          if (quota <= 0) continue

          const queue = varietyGroups[v.id] ?? []

          while (quota > 0 && queue.length > 0) {
            const profile = queue[0]

            // Skip if this profile was manually assigned
            if (manuallyAssignedIds.has(profile.id)) {
              queue.shift()
              continue
            }

            const totalAssignedBoxes = pallets.reduce((sum, pal) => {
              const mhMatch = pal.mhCodes?.find(mh => mh.id === profile.id)
              return sum + (mhMatch?.boxes ?? 0)
            }, 0)

            const totalAssignedWeightKg = totalAssignedBoxes * capacity
            const mainYield = profile.tradeProfile?.availableYield ?? 0
            const extraYield = profile.tradeProfile?.extraAvailableYield ?? 0
            const totalYield = mainYield + extraYield
            const remainingBoxes =
              ((totalYield) -
                (totalAssignedWeightKg ?? 0)) /
              capacity

            if (remainingBoxes <= 0) {
              queue.shift()
              continue
            }

            const assignBoxes = Math.min(remainingBoxes, quota)
            if (assignBoxes <= 0) break

            mhAssignments.push({
              id: profile.id,
              mhCode: profile.tradeProfile?.certificateNumber ?? "",
              boxes: assignBoxes,
            })

            quota -= assignBoxes
            allocated += assignBoxes
            profile.remaining = remainingBoxes - assignBoxes

            if (profile.remaining <= 0) queue.shift()
          }

          varietyGroups[v.id] = queue
        }



        const updatedPallet = {
          ...pallet,
          mhCodes: mhAssignments,
        }

        dispatch(updatePallet(updatedPallet, pIndex))
      }

      setTempSelected([])
      setIsOpen(false)
      setIsReplace(false)
      return
    }

    // Track manually assigned MH codes across all pallets
    const manuallyAssignedIds = new Set<string>()

    for (let pIndex = 0; pIndex < updatedPallets.length; pIndex++) {
      const pallet = updatedPallets[pIndex]
      const meta = productionData?.shipmentPlan?.metadata
        ? JSON.parse(productionData.shipmentPlan.metadata)
        : {}
      const required = pallet.isEuro
        ? meta.euro_pallet_box_count ?? 0
        : meta.regular_pallet_box_count ?? 0

      let alreadyFilled =
        pallet.mhCodes?.reduce((sum, mh) => sum + (mh.boxes ?? 0), 0) ?? 0

      if (alreadyFilled >= required) continue

      let allocated = alreadyFilled
      let mhAssignments: any[] = [...(pallet.mhCodes ?? [])]

      // First, apply manual assignments if any exist for this pallet
      const hasManualAssignments = Object.values(manualAssignments).some(val => val > 0)

      if (hasManualAssignments && pIndex === index) {
        // Only apply manual assignments to the current pallet (index)
        for (const profileId in manualAssignments) {
          const manualBoxes = manualAssignments[profileId]
          if (manualBoxes > 0 && allocated < required) {
            const profile = tempSelected.find(p => p.id === profileId)
            if (profile) {
              const capacity = data?.production?.shipmentPlan?.skuType?.capacity ?? 1
              const totalAssignedBoxes = pallets.reduce((sum, pal) => {
                const mhMatch = pal.mhCodes?.find(mh => mh.id === profileId)
                return sum + (mhMatch?.boxes ?? 0)
              }, 0)
              const totalAssignedWeightKg = totalAssignedBoxes * capacity
              const mainYield = profile.tradeProfile?.availableYield ?? 0
              const extraYield = profile.tradeProfile?.extraAvailableYield ?? 0
              const totalYield = mainYield + extraYield
              const availableBoxes = ((totalYield) - totalAssignedWeightKg) / capacity

              const assignBoxes = Math.min(manualBoxes, availableBoxes, required - allocated)

              if (assignBoxes > 0) {
                mhAssignments.push({
                  id: profileId,
                  mhCode: profile.tradeProfile?.certificateNumber ?? "",
                  boxes: assignBoxes,
                })
                allocated += assignBoxes
                manuallyAssignedIds.add(profileId)

                // Update the variety queue to reflect this assignment
                const varietyId = profile.tradeProfile?.plotCommodity?.variety?.id
                if (varietyId && varietyQueues[varietyId]) {
                  const queueProfile = varietyQueues[varietyId].find(p => p.id === profileId)
                  if (queueProfile) {
                    queueProfile.remaining -= assignBoxes
                  }
                }
              }
            }
          }
        }
      }

      // Then, use automatic distribution for any remaining quota
      const perVariety = required / varieties.length

      for (const v of varieties) {
        let quota = perVariety

        quota = Math.min(quota, required - allocated)
        if (quota <= 0) break

        let queue = varietyQueues[v.id] ?? []

        while (quota > 0 && queue.length > 0) {
          const profile = queue[0]

          // Skip if this profile was manually assigned
          if (manuallyAssignedIds.has(profile.id)) {
            queue.shift()
            continue
          }

          const assign = Math.min(profile.remaining, quota)

          if (assign > 0) {
            mhAssignments.push({
              id: profile.id,
              mhCode: profile.tradeProfile?.certificateNumber ?? "",
              boxes: assign,
            })
            profile.remaining -= assign
            quota -= assign
            allocated += assign
          }

          if (profile.remaining <= 0) queue.shift()
          if (allocated >= required) break
        }

        varietyQueues[v.id] = queue
        if (allocated >= required) break
      }



      const updatedPallet = {
        ...pallet,
        mhCodes: mhAssignments,
      }

      dispatch(updatePallet(updatedPallet, pIndex))
    }

    setTempSelected([])
    setManualAssignments({})
    setIsOpen(false)
  }

  const metadata = productionData?.shipmentPlan?.metadata
    ? JSON.parse(productionData?.shipmentPlan?.metadata)
    : null
  const isEuro = metadata?.is_euro ?? false
  const euroBoxes = metadata?.euro_pallet_box_count ?? 0
  const regularBoxes = metadata?.regular_pallet_box_count ?? 0
  const skuFilled = productionData?.shipmentPlan?.skuFilled ?? 0

  let requiredBoxes = 0
  if (skuFilled > 0) {
    if (isEuro) {
      requiredBoxes = euroBoxes + (skuFilled - 1) * regularBoxes
    } else {
      requiredBoxes = skuFilled * regularBoxes
    }
  }

  return (
    <>
      <FormGroup
        label={
          <>
            MH Code <span className="text-red">*</span>
          </>
        }
        helperText={
          <span className="text-[#1F6D99] text-xs">
            Assigned:{" "}
            {pallet?.mhCodes?.length > 0
              ? pallet.mhCodes.map(v => `${v.mhCode} (${v.boxes?.toFixed(2)})`).join(", ")
              : "Select MH Code"}
          </span>
        }
      >
        <div
          onClick={() => {
            setTempSelected(pallet?.mhCodes ?? [])
            setManualAssignments({})
            setIsOpen(true)
          }}
          style={{ border: "1px solid " }}
          className="flex items-center justify-between cursor-pointer px-3 py-[5.5px] rounded border bg-white border-slate-300"
        >
          <span>
            {pallet?.mhCodes?.length > 0
              ? pallet.mhCodes.map(v => `${v.mhCode}`).join(", ")
              : "Select MH Code"}
          </span>
          <span className="bp4-icon bp4-icon-caret-down" />
        </div>
      </FormGroup>

      <Dialog
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
          setManualAssignments({})
        }}
        title={
          <div className="flex items-center gap-2">
            <Files size={24} /> Select MH Code
          </div>
        }
      >
        <DialogBody className="max-h-[70vh] overflow-y-auto bg-white">
          <div className="bg-blue-50 text-sm p-3 rounded border border-slate-200 my-2">
            <div>
              <b>Packing Format:</b>{" "}
              {productionData?.shipmentPlan?.skuType?.name ?? "--"} |{" "}
              <b>Required Weight:</b>{" "}
              {requiredBoxes * productionData?.shipmentPlan?.skuType?.capacity}{" "}
              kg
            </div>
            <div className="text-gray-600 text-xs">
              {isEuro ? (
                <>
                  Includes <b>1</b> Euro pallet with <b>{euroBoxes}</b> boxes
                  and <b>{skuFilled - 1}</b> regular pallet
                  {skuFilled - 1 > 1 ? "s" : ""} with <b>{regularBoxes}</b>{" "}
                  boxes each.
                </>
              ) : (
                <>
                  Includes <b>{skuFilled}</b> regular pallet
                  {skuFilled > 1 ? "s" : ""}, each with <b>{regularBoxes}</b>{" "}
                  boxes.
                </>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {approvedProfiles.map(profile => {
              const mhCode = profile.tradeProfile?.certificateNumber ?? "-"
              const farmer =
                profile.tradeProfile?.plotCommodity?.plot?.farmer?.fullName ??
                "-"
              const location =
                profile.tradeProfile?.plotCommodity?.plot?.farmer?.address
                  ?.address ?? "-"
              const capacity = data?.production?.shipmentPlan?.skuType?.capacity ?? 1
              const mainYield = profile.tradeProfile?.availableYield ?? 0
              const extraYield = profile.tradeProfile?.extraAvailableYield ?? 0
              const totalYield = mainYield + extraYield

              const boxes = totalYield / (capacity || 1)
              const plotBoxes =
                (profile.tradeProfile?.plotYeild ?? 0) / (capacity || 1)

              const totalAssignedBoxes = pallets.reduce((sum, pallet) => {
                const mhMatch = pallet.mhCodes?.find(mh => mh.id === profile.id)
                return sum + (mhMatch?.boxes ?? 0)
              }, 0)

              const totalAssignedWeightKg = totalAssignedBoxes * capacity
              const disabled = assignedMhCodes.includes(profile.id) || totalYield <= totalAssignedWeightKg

              return (
                <div
                  key={profile.id}
                  style={{ border: "1px solid" }}
                  className={`flex flex-col gap-2 p-5 rounded-lg border ${disabled
                    ? "border-gray-300 bg-gray-100 opacity-60"
                    : "border-slate-200"
                    }`}
                >
                  <div className="flex items-start justify-between w-full">
                    <Checkbox
                      disabled={disabled}
                      checked={tempSelected.some(p => p.id === profile.id)}
                      onChange={() => toggleSelect(profile)}
                    />
                    <div className="space-y-2 w-full">
                      <div className="flex gap-2 justify-between items-center">
                        <b>{mhCode}</b>
                        <CustomTag intent="primary" minimal title="Assigned">
                          Available Yield: {(totalYield - (totalAssignedWeightKg ?? 0)).toFixed(2)} kg / {(boxes - totalAssignedBoxes).toFixed(2)} boxes
                        </CustomTag>
                      </div>
                      <div>
                        <div>{farmer}</div>
                        <div className="text-gray-500 text-xs">{location}</div>
                      </div>

                      <div className="flex gap-2 justify-between items-start">
                        <div>
                          <div className="text-gray-700 text-sm">
                            {
                              profile?.tradeProfile?.plotCommodity?.variety
                                ?.name
                            }
                          </div>
                          <div className="text-gray-500 text-xs">Variety</div>
                        </div>
                        <div>
                          <div className="text-orange-600 font-bold">
                            {totalAssignedWeightKg.toFixed(2)} kg / {totalYield.toFixed(2)} kg
                          </div>
                          <div className="text-gray-500 text-xs">
                            {totalAssignedBoxes?.toFixed(0)} boxes / {boxes.toFixed(0)} boxes
                          </div>
                          <div className="text-blue-600 text-xs">
                            Main: {mainYield.toFixed(2)} kg | Extra: {extraYield.toFixed(2)} kg
                          </div>
                        </div>
                      </div>

                      {tempSelected.some(p => p.id === profile.id) && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assign Boxes
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={(boxes - totalAssignedBoxes).toFixed(2)}
                            step="0.01"
                            placeholder={`Max: ${(boxes - totalAssignedBoxes).toFixed(2)} boxes`}
                            value={manualAssignments[profile.id] ?? ""}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0
                              const maxBoxes = boxes - totalAssignedBoxes
                              setManualAssignments(prev => ({
                                ...prev,
                                [profile.id]: Math.min(Math.max(0, value), maxBoxes)
                              }))
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            Available: {(boxes - totalAssignedBoxes).toFixed(2)} boxes ({(totalYield - totalAssignedWeightKg).toFixed(2)} kg)
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </DialogBody>

        <DialogFooter>
          <div className="flex flex-col w-full gap-2">


            <div className="flex justify-between items-center w-full">
              <div className="flex gap-2">
                <CustomButton onClick={() => setIsOpen(false)} round>
                  Cancel
                </CustomButton>
                {Object.keys(manualAssignments).length > 0 && (
                  <CustomButton
                    onClick={() => setManualAssignments({})}
                    intent="warning"
                    round
                  >
                    Clear Assignments
                  </CustomButton>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <Checkbox
                  label="Is Replace MhCode"
                  checked={isReplace}
                  onChange={e => setIsReplace(!isReplace)}
                />
                <CustomButton
                  intent="success"
                  onClick={
                    mixConfirmSelection
                  }
                  round
                >
                  Confirm
                </CustomButton>

              </div>

            </div>
          </div>
        </DialogFooter>
      </Dialog>
    </>
  )
}

export default MHCodeSelector
